/**
 * Per-task detail slide-over. Mirrors mobile's task screen
 *   /Users/gudupuramesh/Documents/siteexpens/app/(app)/projects/[id]/task/[taskId].tsx
 *
 * Sections (top → bottom):
 *   1. Hero — title + status pill + category + dates + assignee
 *   2. Progress bar with %
 *   3. Description (when present)
 *   4. Reference photos (task.photoUris, click → opens in new tab)
 *   5. Updates feed — every work log posted against this task,
 *      newest first. Avatar + author + time-ago + progress delta
 *      + note + thumbnail grid
 *
 * Footer action bar (sticky):
 *   - Post update (primary) → opens LogWorkSlideOver
 *   - Edit task (secondary) → opens TaskFormSlideOver
 *
 * Click a row on Calendar / List / Board → opens THIS (was opening
 * the edit form directly). The detail has its own Edit button to
 * reach the form when needed.
 */
import { useEffect, useMemo, useState } from 'react';

import { canEditTasks } from '@shared/perms';

import { LogWorkSlideOver } from '@/components/LogWorkSlideOver';
import { TaskFormSlideOver } from '@/components/TaskFormSlideOver';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import {
  taskStatusLabel,
  type TaskItem,
} from '@/lib/useProjectTasks';
import {
  relativeTime,
  type TaskUpdateDoc,
} from '@/lib/useProjectTaskUpdatesForDate';
import { useTaskUpdates } from '@/lib/useTaskUpdates';

const STATUS_TINT = {
  completed: { bg: 'var(--success-bg)', fg: 'var(--success-text)' },
  ongoing: { bg: 'var(--warning-bg)', fg: 'var(--warning-text)' },
  not_started: { bg: '#F3F4F6', fg: 'var(--text-secondary)' },
} as const;

const CATEGORY_LABEL: Record<string, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  woodwork: 'Woodwork',
  design: 'Design',
  cleaning: 'Cleaning',
  floor_cleaning: 'Floor Cleaning',
  floor_matting: 'Floor Matting',
  painting: 'Painting',
  false_ceiling: 'False Ceiling',
  tiling: 'Tiling',
  general: 'General',
};
const CATEGORY_COLOR: Record<string, { bg: string; fg: string }> = {
  electrical: { bg: '#DBEAFE', fg: '#1D4ED8' },
  plumbing: { bg: '#D1FAE5', fg: '#065F46' },
  woodwork: { bg: '#FEF3C7', fg: '#92400E' },
  design: { bg: '#FCE7F3', fg: '#BE185D' },
  cleaning: { bg: '#CFFAFE', fg: '#0E7490' },
  floor_cleaning: { bg: '#CFFAFE', fg: '#0E7490' },
  floor_matting: { bg: '#ECFCCB', fg: '#3F6212' },
  painting: { bg: '#FFEDD5', fg: '#9A3412' },
  false_ceiling: { bg: '#FFEDD5', fg: '#9A3412' },
  tiling: { bg: '#FEE2E2', fg: '#B91C1C' },
  general: { bg: '#F3F4F6', fg: '#374151' },
};

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function authorInitial(name: string | undefined): string {
  const c = (name ?? '').trim().charAt(0);
  return c ? c.toUpperCase() : '?';
}

// ───── Component ─────

export function TaskDetailSlideOver({
  open,
  onClose,
  orgId,
  projectId,
  task,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  projectId: string;
  /** When null, panel renders empty state. */
  task: TaskItem | null;
}) {
  const { data: updates, loading: updatesLoading } = useTaskUpdates(
    task?.id,
  );
  const { role: orgRole } = useCurrentOrgRole();
  const canEdit = canEditTasks(orgRole);
  const [editOpen, setEditOpen] = useState(false);
  const [postUpdateOpen, setPostUpdateOpen] = useState(false);

  // Body scroll lock + Esc to close (only when nothing is layered
  // on top — Esc otherwise closes the layered child).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editOpen && !postUpdateOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, editOpen, postUpdateOpen]);

  const status =
    task?.status === 'completed' ? 'completed'
    : task?.status === 'ongoing' ? 'ongoing'
    : 'not_started';
  const statusTint = STATUS_TINT[status];
  const catKey = task?.category ?? 'general';
  const catLabel = CATEGORY_LABEL[catKey] ?? catKey.replace(/_/g, ' ');
  const catColor = CATEGORY_COLOR[catKey] ?? CATEGORY_COLOR.general;
  const progress = useMemo(
    () => Math.max(0, Math.min(100, task?.progress ?? 0)),
    [task?.progress],
  );

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-check-square" /> Task
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body" style={{ paddingBottom: 96 }}>
          {!task ? (
            <div
              style={{
                textAlign: 'center',
                padding: 32,
                color: 'var(--text-tertiary)',
                fontSize: 13,
              }}
            >
              Task not found.
            </div>
          ) : (
            <>
              {/* Hero */}
              <div className="card" style={{ padding: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: 'var(--text-primary)',
                      lineHeight: 1.3,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {task.title}
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '3px 8px',
                      borderRadius: 4,
                      background: statusTint.bg,
                      color: statusTint.fg,
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {taskStatusLabel(task.status)}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    alignItems: 'center',
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    marginBottom: 12,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: catColor.bg,
                      color: catColor.fg,
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                    }}
                  >
                    {catLabel}
                  </span>
                  <span>
                    <i className="ph ph-calendar-blank" />{' '}
                    {fmtDate(task.startDate?.toMillis?.() ?? null)} →{' '}
                    {fmtDate(task.endDate?.toMillis?.() ?? null)}
                  </span>
                  {task.assignedToName ? (
                    <span>
                      <i className="ph ph-user" /> {task.assignedToName}
                    </span>
                  ) : null}
                </div>
                {/* Progress */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div
                    className="spent-bar"
                    style={{ flex: 1, height: 6 }}
                  >
                    <div
                      className="spent-bar-fill"
                      style={{
                        width: `${progress}%`,
                        background:
                          status === 'completed'
                            ? 'var(--success)'
                            : status === 'ongoing'
                              ? 'var(--brand)'
                              : 'var(--border)',
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: 'var(--text-secondary)',
                      fontVariantNumeric: 'tabular-nums',
                      minWidth: 36,
                      textAlign: 'right',
                    }}
                  >
                    {progress}%
                  </span>
                </div>
              </div>

              {/* Description */}
              {task.description ? (
                <div>
                  <SectionTitle>Description</SectionTitle>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--text-secondary)',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      padding: 12,
                      background: '#FAFAFA',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                    }}
                  >
                    {task.description}
                  </div>
                </div>
              ) : null}

              {/* Reference photos */}
              {(task.photoUris ?? []).length > 0 ? (
                <div>
                  <SectionTitle>
                    Reference photos · {task.photoUris!.length}
                  </SectionTitle>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    {task.photoUris!.map((url) => (
                      <a
                        key={url}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          width: 72,
                          height: 72,
                          borderRadius: 6,
                          overflow: 'hidden',
                          display: 'block',
                          border: '1px solid var(--border)',
                        }}
                      >
                        <img
                          src={url}
                          alt="Reference"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Updates feed */}
              <div>
                <SectionTitle>
                  Work log · {updates.length} update
                  {updates.length === 1 ? '' : 's'}
                </SectionTitle>
                {updatesLoading ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: 'center',
                      color: 'var(--text-tertiary)',
                      fontSize: 13,
                    }}
                  >
                    Loading work log…
                  </div>
                ) : updates.length === 0 ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: 'center',
                      fontSize: 13,
                      color: 'var(--text-tertiary)',
                      border: '1px dashed var(--border)',
                      borderRadius: 8,
                    }}
                  >
                    No updates posted on this task yet. Use the{' '}
                    <b>Post update</b> button below to log your first.
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}
                  >
                    {updates.map((u, i) => (
                      <UpdateRow
                        key={u.id}
                        update={u}
                        previousProgress={previousProgress(updates, i)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Sticky footer actions */}
        {task ? (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              background: '#fff',
              borderTop: '1px solid var(--border)',
              padding: 12,
              display: 'flex',
              gap: 8,
              boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
            }}
          >
            {canEdit ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setEditOpen(true)}
                style={{ justifyContent: 'center', flex: 1 }}
              >
                <i className="ph ph-pencil-simple" /> Edit task
              </button>
            ) : null}
            <button
              type="button"
              className="btn-primary"
              onClick={() => setPostUpdateOpen(true)}
              style={{ justifyContent: 'center', flex: 1 }}
            >
              <i className="ph-bold ph-plus" /> Post update
            </button>
          </div>
        ) : null}
      </div>

      {/* Layered children */}
      <TaskFormSlideOver
        open={editOpen}
        onClose={() => setEditOpen(false)}
        orgId={orgId}
        projectId={projectId}
        task={task}
      />
      <LogWorkSlideOver
        open={postUpdateOpen}
        onClose={() => setPostUpdateOpen(false)}
        task={task}
      />
    </>
  );
}

// ───── Helpers + sub-components ─────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: 8,
        paddingBottom: 6,
        borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
    </div>
  );
}

/** Find the next-older update for the same task (= the update
 *  immediately AFTER this one in the newest-first list). */
function previousProgress(all: TaskUpdateDoc[], index: number): number {
  for (let j = index + 1; j < all.length; j++) return all[j].progress;
  return 0;
}

function UpdateRow({
  update,
  previousProgress: prev,
}: {
  update: TaskUpdateDoc;
  previousProgress: number;
}) {
  const delta = update.progress - prev;
  const time = relativeTime(update.createdAt?.toMillis?.() ?? null);
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: 12,
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: '#fff',
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'var(--brand-bg)',
          color: 'var(--brand)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {authorInitial(update.authorName)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {update.authorName}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            {time}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 4,
              background:
                delta > 0
                  ? 'var(--success-bg)'
                  : delta < 0
                    ? 'var(--critical-bg)'
                    : '#F3F4F6',
              color:
                delta > 0
                  ? 'var(--success-text)'
                  : delta < 0
                    ? 'var(--critical-text)'
                    : 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {delta > 0 ? `+${delta}%` : `${update.progress}%`}
          </span>
        </div>
        {update.text ? (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              lineHeight: 1.5,
              marginTop: 4,
              whiteSpace: 'pre-wrap',
            }}
          >
            {update.text}
          </div>
        ) : null}
        {update.photoUris.length > 0 ? (
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginTop: 8,
              flexWrap: 'wrap',
            }}
          >
            {update.photoUris.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 4,
                  overflow: 'hidden',
                  display: 'block',
                  background: '#E5E7EB',
                  border: '1px solid var(--border)',
                }}
              >
                <img
                  src={url}
                  alt="Work log"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </a>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
